// frontend/src/components/StoresTab.js
import { KINDS } from "@/lib/catalogRules";
import { Field, SectionCard, TextInput } from "@/components/shared";

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function uniqueFormatId(label, formats) {
  const base = slugify(label) || "format";
  const ids = new Set((formats || []).map((f) => f.id));
  if (!ids.has(base)) return base;
  let i = 2;
  while (ids.has(`${base}-${i}`)) i += 1;
  return `${base}-${i}`;
}

function StoreEditor({ store, onUpdate, onDelete }) {
  const update = (field, value) => onUpdate({ ...store, [field]: value });

  return (
    <div className="leadline-border-thin bg-white p-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Name">
          <TextInput value={store.name} onChange={(v) => update("name", v)} />
        </Field>
        <Field label="Kind">
          <select
            value={store.kind}
            onChange={(e) => update("kind", e.target.value)}
            className="w-full leadline-border-thin px-2 py-1 bg-white text-sm"
          >
            {KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Store URL">
        <TextInput value={store.storeUrl} onChange={(v) => update("storeUrl", v)} placeholder="https://..." />
      </Field>
      <button onClick={onDelete} className="text-xs text-red-600 hover:underline mt-1">
        Delete store
      </button>
    </div>
  );
}

function FormatEditor({ format, onUpdate }) {
  const update = (field, value) => onUpdate({ ...format, [field]: value });

  return (
    <div className="leadline-border-thin bg-white p-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="ID">
          <input
            value={format.id}
            readOnly
            className="w-full leadline-border-thin px-2 py-1 bg-[#F5EDE0] text-sm text-[#3A3530]"
          />
        </Field>
        <Field label="Label">
          <TextInput value={format.label} onChange={(v) => update("label", v)} />
        </Field>
      </div>
    </div>
  );
}

function StoresTab({ orgConfig, onChange }) {
  const stores = orgConfig?.stores || [];
  const formats = orgConfig?.formats || [];

  const updateConfig = (patch) => onChange({ ...orgConfig, ...patch });

  const updateStore = (index, updated) => {
    const nextStores = stores.map((s, i) => (i === index ? updated : s));
    updateConfig({ stores: nextStores });
  };

  const addStore = (kind) => {
    const id = `store-${Date.now()}`;
    updateConfig({
      stores: [...stores, { id, name: "New Store", kind, storeUrl: "" }],
    });
  };

  const deleteStore = (index) => {
    const store = stores[index];
    if (!window.confirm(`Delete store "${store.name}"?`)) return;
    updateConfig({ stores: stores.filter((_, i) => i !== index) });
  };

  const updateFormat = (index, updated) => {
    const nextFormats = formats.map((f, i) => (i === index ? updated : f));
    updateConfig({ formats: nextFormats });
  };

  const addFormat = () => {
    const label = "New Format";
    const id = uniqueFormatId(label, formats);
    updateConfig({ formats: [...formats, { id, label }] });
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Stores" accentColor="#2B7D6E">
        <div className="space-y-2">
          {stores.map((store, index) => (
            <StoreEditor
              key={store.id}
              store={store}
              onUpdate={(updated) => updateStore(index, updated)}
              onDelete={() => deleteStore(index)}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            onClick={() => addStore("digital")}
            className="leadline-border-thin px-3 py-1 text-xs bg-white text-[#3A3530] hover:bg-[#F5EDE0]"
          >
            + Add digital store
          </button>
          <button
            onClick={() => addStore("other")}
            className="leadline-border-thin px-3 py-1 text-xs bg-white text-[#3A3530] hover:bg-[#F5EDE0]"
          >
            + Add other store
          </button>
        </div>
      </SectionCard>

      <SectionCard title="Formats" accentColor="#E8A33A">
        <div className="space-y-2">
          {formats.map((format, index) => (
            <FormatEditor
              key={format.id}
              format={format}
              onUpdate={(updated) => updateFormat(index, updated)}
            />
          ))}
        </div>
        <button
          onClick={addFormat}
          className="leadline-border-thin px-3 py-1 text-xs bg-white text-[#3A3530] mt-2 hover:bg-[#F5EDE0]"
        >
          + Add format
        </button>
      </SectionCard>
    </div>
  );
}

export default StoresTab;
